'use client';

import React from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { defaultLocale } from './locales';
import { useResolvedLocale } from './useResolvedLocale';
import HtmlLangUpdater from '@/components/common/HtmlLangUpdater';

// Auth and onboarding only. Page catalogs load with the product shell.
import enCommon from '@/messages/en/common.json';
import enAuth from '@/messages/en/auth.json';
import enError from '@/messages/en/error.json';
import esCommon from '@/messages/es/common.json';
import esAuth from '@/messages/es/auth.json';
import esError from '@/messages/es/error.json';
import ptCommon from '@/messages/pt/common.json';
import ptAuth from '@/messages/pt/auth.json';
import ptError from '@/messages/pt/error.json';
import zhCommon from '@/messages/zh/common.json';
import zhAuth from '@/messages/zh/auth.json';
import zhError from '@/messages/zh/error.json';
import hiCommon from '@/messages/hi/common.json';
import hiAuth from '@/messages/hi/auth.json';
import hiError from '@/messages/hi/error.json';
import deCommon from '@/messages/de/common.json';
import deAuth from '@/messages/de/auth.json';
import deError from '@/messages/de/error.json';
import viCommon from '@/messages/vi/common.json';
import viAuth from '@/messages/vi/auth.json';
import viError from '@/messages/vi/error.json';
import frCommon from '@/messages/fr/common.json';
import frAuth from '@/messages/fr/auth.json';
import frError from '@/messages/fr/error.json';
import ruCommon from '@/messages/ru/common.json';
import ruAuth from '@/messages/ru/auth.json';
import ruError from '@/messages/ru/error.json';
import itCommon from '@/messages/it/common.json';
import itAuth from '@/messages/it/auth.json';
import itError from '@/messages/it/error.json';
import jaCommon from '@/messages/ja/common.json';
import jaAuth from '@/messages/ja/auth.json';
import jaError from '@/messages/ja/error.json';
const messages: Record<string, any> = {
  en: {
    ...enCommon,
    ...enAuth,
    ...enError
  },
  es: {
    ...esCommon,
    ...esAuth,
    ...esError
  },
  pt: {
    ...ptCommon,
    ...ptAuth,
    ...ptError
  },
  zh: {
    ...zhCommon,
    ...zhAuth,
    ...zhError
  },
  hi: {
    ...hiCommon,
    ...hiAuth,
    ...hiError
  },
  de: {
    ...deCommon,
    ...deAuth,
    ...deError
  },
  vi: {
    ...viCommon,
    ...viAuth,
    ...viError
  },
  fr: {
    ...frCommon,
    ...frAuth,
    ...frError
  },
  ru: {
    ...ruCommon,
    ...ruAuth,
    ...ruError
  },
  it: {
    ...itCommon,
    ...itAuth,
    ...itError
  },
  ja: {
    ...jaCommon,
    ...jaAuth,
    ...jaError
  }
};

/** Light catalog for signup, login, and onboarding. Does not include pages.json. */
export default function AuthI18nProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const locale = useResolvedLocale();
  const currentMessages = messages[locale] ?? messages[defaultLocale];
  return <NextIntlClientProvider locale={locale} messages={currentMessages} onError={error => {
    if (error.code === 'ENVIRONMENT_FALLBACK') return;
    console.error(error);
  }} data-sentry-element="NextIntlClientProvider" data-sentry-component="AuthI18nProvider" data-sentry-source-file="AuthI18nProvider.tsx">
      <HtmlLangUpdater data-sentry-element="HtmlLangUpdater" data-sentry-source-file="AuthI18nProvider.tsx" />
      {children}
    </NextIntlClientProvider>;
}